import { action, computed, makeObservable, observable, reaction } from "mobx";
import { FilterConfiguration } from "../config/filter-configuration";

interface DropdownOption {
    key: string;                  // Identifier for the dropdown
    value: string | number;       // Coded value for querying (only in case of a field with domain)
    text: string;                 // Human-readable value to display
}

interface DropdownSettings {
    fieldName: string;
    fieldAlias: string;
    options: DropdownOption[];
    searchable: boolean;
}

interface filterSettings {
    dataset:  string;
    layerId: number;
    dropdownSettings: DropdownSettings[];
}

interface queryParams {
    [layerId: number]: queryParamValues;
}

interface queryParamValues {
    [fieldName: string]: string[] | number[];
}

interface defExp {
    layerId: number;
    query: string;
}

interface featureCounts {
    [fieldName: string]: number;
}

export class FilterStore {
    @observable queryParams: queryParams = {};

    @observable filterSettings: filterSettings[] = [];

    @observable isOptionsLoading: boolean = false; // Indicates if options are being fetched

    @observable filteredExtent: __esri.Extent | null = null; // Combined extent of all selected features

    @observable isExtentChanged: boolean = false; // Tracks if the current extent differs from the filteredExtent

    @observable selectionHasChanged: boolean = false; // Indicates whether the selection has changed

    @observable filterIsActive: boolean = false; // Indicates whether some features have been filtered

    @observable mapViewExtent: __esri.Extent | null = null; // Stores mapView's extent internally

    @observable isManualNavigation: boolean; // Track whether is manual navigation or performed by mapView.goTo()

    @observable selectedFeatures: number | null = null;

    @observable featureCounts: featureCounts = {};

    constructor() {
        makeObservable(this);

        // Reaction to track extent change
        reaction(
            () => [this.mapViewExtent, this.filteredExtent], // Observe both extents
            ([currentMapExtent, combined]) => {

                // Skip reaction logic if manual navigation is not in progress
                if (!this.isManualNavigation) {
                    return;
                }

                if (!currentMapExtent || !combined) {
                    this.setExtentChanged(false);
                    return;
                }
                this.setExtentChanged(!currentMapExtent.equals(combined));
            }
        );
    }

    @computed get hasSelection(): boolean {

        return Object.values(this.queryParams).some((queryParam) =>
            Object.values(queryParam).some((values) => Array.isArray(values) &&
                values.length > 0)
        );
    }

    @computed get queryParamsAreEmpty(): boolean {

        // Check if queryParams is an empty object
        if (Object.keys(this.queryParams).length === 0) {
            return true;
        }

        // Check if all elements are empty arrays
        return Object.values(this.queryParams).every((queryParam) =>
            Object.values(queryParam).every((values: string[] | number[]) => values.length === 0)
        );
    }

    @computed get definitionExpressions(): defExp[] {

        return this.filterSettings.map((setting) => {
            const layerId = setting.layerId;

            // Get the fields for this layerId from queryParams or use an empty object
            const fields = this.queryParams[layerId] || {};

            // Build the SQL conditions for this layer
            const conditions = Object.entries(fields)
                .filter(([, values]) => values.length > 0)
                .map(([fieldName, values]) => {
                    // Convert values into SQL-like conditions
                    const valueList = values
                        .map((v) => (typeof v === "string" ? `'${v}'` : v))
                        .join(", ");
                    return `${fieldName} IN (${valueList})`;
                });

            // If no conditions exist, default to "1=1"
            const query = conditions.length === 0 ? "1=1" : conditions.join(" AND ");

            if (conditions.length === 0) {
                this.setSelectedFeatures(null);
            }

            return {
                layerId,
                query,
            };
        });
    }

    @action setSelectionHasChanged(value: boolean) {
        this.selectionHasChanged = value;
    }

    @action setFilterIsActive(value: boolean) {
        this.filterIsActive = value;
    }

    @action setQueryParam(layerId: number, fieldName: string, values: string[] | number[]) {

        if (!this.queryParams[layerId]) {
            // Initialize the layerId entry if it doesn't exist
            this.queryParams[layerId] = {};
        }

        // Update the specific field within the layer
        this.queryParams[layerId][fieldName] = values;
        this.setSelectionHasChanged(true);
    }

    @action setFilterSettings(options: filterSettings[]) {
        this.filterSettings = options;
        this.isOptionsLoading = false;
    }

    @action setFilteredExtent(extent: __esri.Extent | null) {
        this.filteredExtent = extent;
    }

    @action updateMapViewExtent(newExtent: __esri.Extent) {
        this.mapViewExtent = newExtent;
    }

    @action setExtentChanged(value: boolean) {
        this.isExtentChanged = value;
    }

    @action setManualNavigation(value: boolean) {
        this.isManualNavigation = value;
    }

    @action setSelectedFeatures(value: number | null) {
        this.selectedFeatures = value;
    }

    @action setFeatureCounts(attr: string, value: number) {
        this.featureCounts[attr] = value;
    }

    /**
     * Load dropdown options by using passed filter configuration and map-image.
     */
    @action async loadFilterSettings(
        filterConfig: FilterConfiguration,
        mapImage: __esri.MapImageLayer
    ) {
        this.isOptionsLoading = true;

        try {
            // Call private helper method to process and fetch dropdown options
            const options = await this.convertFilterConfigToFilterSettings(filterConfig, mapImage);
            this.setFilterSettings(options);
        } catch (err: any) {
            throw new Error("Failed to load dropdown options: " + err.message);
        }
    }

    @action async loadFeatureCounts(
        filterConfig: FilterConfiguration,
        mapImage: __esri.MapImageLayer
    ) {
        for (const dataset of filterConfig.datasets) {

            const subLayer = mapImage.sublayers?.find((subL: any) => subL.id === dataset.layerId);

            if (mapImage && subLayer) {
                const query = subLayer.createQuery();
                query.where = "1=1";

                const count = await subLayer.queryFeatureCount(query);

                this.setFeatureCounts(subLayer.title!, count);
            }
        }
    }

    @action resetFilters = (): void => {
        // Clear all selected options in the dropdowns
        this.queryParams = {};
        this.setFilterIsActive(false);
    };

    @action makeQuery = async (
        mapImage: __esri.MapImageLayer,
        filterConfig: FilterConfiguration,
        mapView: __esri.MapView | null = null) => {

        let combinedExtent = null; // To store the combined extent of all features

        for (const { layerId, query } of this.definitionExpressions) {

            const subLayer = mapImage.sublayers?.find((sl: any) => sl.id === layerId);

            if (subLayer) {
                subLayer.definitionExpression = query;

                if (!subLayer.visible && query !== "1=1") {
                    subLayer.visible = true;
                }

                // Query the features that match the filter
                const queryResponse = await subLayer.queryFeatures({
                    where: query,
                    returnGeometry: true,
                    outSpatialReference: mapImage.spatialReference,
                });

                const { features } = queryResponse;

                const dataset = filterConfig.datasets.find((el) => el.layerId === layerId);
                const isZoom = dataset?.isZoom;

                this.setSelectedFeatures(
                    this.selectedFeatures
                        ? this.selectedFeatures + features.length
                        : features.length
                );

                this.setFeatureCounts(subLayer.title!, features.length);

                if (features.length > 0 && isZoom && query !== "1=1") {
                    const featureExtents = features.map((feature: any) => feature.geometry.extent);
                    const layerExtent = featureExtents.reduce((acc: any, currentExtent: any) =>
                        acc ? acc.union(currentExtent) : currentExtent
                    );

                    // Combine with other extents
                    combinedExtent = combinedExtent ? combinedExtent.union(layerExtent) : layerExtent;
                }
            }
        }

        if (mapView) {
            combinedExtent = combinedExtent ? combinedExtent.expand(1.1) : combinedExtent;
            this.setFilteredExtent(combinedExtent);
            this.setSelectionHasChanged(false);
            this.setFilterIsActive(!this.queryParamsAreEmpty);

            if (combinedExtent !== null) {

                this.setManualNavigation(false);

                try {
                    await mapView.goTo(combinedExtent); // Zoom to the extent with some buffer
                    this.mapViewExtent = combinedExtent;
                    this.setExtentChanged(false);
                } finally {
                    this.setManualNavigation(true);
                }
            }
        }
    };

    /**
     * Converts configuration and sublayer data into dropdown options.
     */
    private async convertFilterConfigToFilterSettings(
        filterConfig: FilterConfiguration,
        mapImage: __esri.MapImageLayer
    ): Promise<filterSettings[]> {

        const result: filterSettings[] = [];

        for (const dataset of filterConfig.datasets) {

            const subLayer = mapImage.sublayers?.find((subL: any) => subL.id === dataset.layerId);

            if (subLayer) {

                const dropdownSettings: DropdownSettings[] = [];

                for (const setting of dataset.settings) {

                    const { fieldAlias, values } = await this.getFieldValues(
                        subLayer,
                        setting.field,
                        setting.fieldAlias,
                        setting.orderValues,
                        setting.sortByText,
                        setting.descending
                    );

                    dropdownSettings.push({
                        fieldName: setting.field, // Field name used for backend queries
                        fieldAlias, // Alias to display
                        options: values.map(({ codedValue, text }) => ({
                            key: text.toLowerCase().replace(/[\s\W]+/g, "_"),
                            value: codedValue,
                            text
                        })),
                        searchable: setting.searchable
                    });

                }
                result.push({
                    dataset: subLayer.title!,
                    layerId: dataset.layerId,
                    dropdownSettings
                });
            }
        }

        return result;
    }

    /**
     * Queries distinct field values from a given sublayer and field.
     */
    private async getFieldValues(
        subLayer: __esri.Sublayer,
        fieldName: string,
        fieldAlias: string,
        orderValues: boolean,
        sortByText: boolean,
        descending: boolean
    ): Promise<{ fieldAlias: string; values: Array<{ codedValue: string | number; text: string}> }> {

        if (subLayer.loadStatus !== "loaded") {
            await subLayer.load();
        }

        const fieldInfo = subLayer.fields?.find((field) => field.name === fieldName);

        if (!fieldInfo) {
            throw new Error(`Field '${fieldName}' not found in layer '${subLayer.title}'`);
        }

        // Use alias provided in settings or fallback to alias in layer or field name
        fieldAlias = fieldAlias || fieldInfo.alias || fieldName;

        let values: Array<{ codedValue: string | number; text: string }>;

        // Make query to get all unique values
        const query = subLayer.createQuery();
        query.where = "1=1"; // Fetch all features
        query.outFields = [fieldName];
        query.returnDistinctValues = true;
        query.returnGeometry = false;

        const queryResults = await subLayer.queryFeatures(query);

        if (fieldInfo.domain && fieldInfo.domain.type === "coded-value") {
            // The field has a codedValue domain
            const domain = fieldInfo.domain;

            // Map the unique coded values to their domain names
            values = queryResults.features.map((feature: any) => {
                const codedValue = feature.attributes[fieldName];

                // Find the domain value that matches the codedValue
                const domainValue = domain.codedValues.find(
                    (item: { code: any; name: string }) => item.code === codedValue
                );

                // If the coded value exists in the domain, use the name; otherwise, fall back to the codedValue
                const text = domainValue ? domainValue.name : String(codedValue);

                return {
                    codedValue,
                    text, // Update the text to the domain name if available
                };
            });
        } else {
            // If there's no domain, or it's not a codedValue domain, return the raw coded value
            values = queryResults.features.map((feature: any) => ({
                codedValue: feature.attributes[fieldName],
                text: feature.attributes[fieldName], // No domain, so text is the same as codedValue
            }));
        }

        if (orderValues) {
            values = [...values].sort((a, b) => {
                const valA = sortByText ? a.text : a.codedValue;
                const valB = sortByText ? b.text : b.codedValue;

                let comparison: number;

                if (typeof valA === "string" && typeof valB === "string") {
                    comparison = valA.localeCompare(valB);
                } else {
                    comparison = valA < valB ? -1 : valA > valB ? 1 : 0;
                }

                return descending ? -comparison : comparison;
            });
        }

        return { fieldAlias, values};
    }
}
